import React, { useContext, useState, createContext, useCallback, useEffect, useMemo } from 'react'

import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { getCompleteUserProfile, getSupabaseSession } from '../utils/helpers/supabase.helpers'
import { User } from '../types/supabase'

interface SessionContextType {
  refresh: (target?: string) => void
  loading: boolean
  user?: User
  checkFeatureAuth: (feature: string) => boolean
}

const SessionContext = createContext<SessionContextType>({
  refresh: () => {},
  loading: true,
  user: undefined,
  checkFeatureAuth: () => false,
})

export const useSessionContext = () => useContext(SessionContext)

interface SessionContextProviderProps {
  children: React.ReactNode
}

export const SessionContextProvider: React.FC<SessionContextProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>()

  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)

  const refetchUser = useCallback(async () => {
    setLoading(true)

    const { data: session } = await getSupabaseSession()
    if (!session?.session) {
      setUser(undefined)
      setLoading(false)
      return
    }

    const { data: profile, error: profileError } = await getCompleteUserProfile()

    if (!profile || profileError) {
      setUser(undefined)
      Sentry.setUser(null)
      setLoading(false)
      return
    }

    // we can only set ID for privacy compliances
    Sentry.setUser({
      id: (session.session as { user: { id: string } })?.user?.id,
    })

    setUser({
      ...(profile as User),
      confirmedAt: (session.session as { user: { confirmed_at: string } })?.user?.confirmed_at,
    })

    setLoading(false)
  }, [])

  const checkFeatureAuth = useCallback(
    (feature: string) => {
      if (!user?.features) return false
      return user.features?.includes(feature)
    },
    [user]
  )

  const refresh = (target = '/') => {
    refetchUser()
    navigate(target)
  }

  useEffect(() => {
    refetchUser()
  }, [refetchUser])

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      refresh,
      checkFeatureAuth,
    }),
    [user, loading, refresh, checkFeatureAuth]
  )

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>
}
